import { useEffect, useState } from 'react';
import { Caption1, Field, Spinner, makeStyles, useId } from '@fluentui/react-components';
import { acceptAttribute, scanPermitsUse, type HrUploadStatus, type ScanState } from '@vigeo/shared';
import { tokens } from '@vigeo/ui';
import { stageUpload, useUploadStatus, type StagedUpload, type UploadTarget } from '@/lib/hr-uploads';

const useStyles = makeStyles({
  status: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS },
});

/** What HR is told about their own upload. Nothing is saved or published on a file until it is clean. */
const wording: Record<ScanState, string> = {
  pending: 'Uploaded. Checking it for malware — you can save once it is clean.',
  scanning: 'Uploaded. Checking it for malware — you can save once it is clean.',
  unavailable: 'Uploaded. The malware scan is retrying — you can save once it is clean.',
  clean: 'Scanned clean. Ready to save.',
  infected: 'Blocked by the malware scan. This file will never be stored — upload a different copy.',
  scan_failed: 'The malware scan could not check this file. Upload it again.',
};

export interface HrUpload {
  readonly uploadId: string;
  readonly recordId?: string;
  readonly fileName: string;
  /** Of the accepted bytes. A template publish names it. */
  readonly sha256: string | null;
  /** Of the accepted bytes, for the attached-file summary a caller may draw (#260). */
  readonly byteLength: number;
  /** True only once the scan is `clean`. */
  readonly usable: boolean;
}

export interface HrFileUploadProps {
  readonly label: string;
  readonly hint?: string;
  readonly required?: boolean;
  readonly target: UploadTarget;
  readonly acceptedTypes: readonly string[];
  readonly disabled?: boolean;
  /** Called with the upload as its scan moves, and null when it is replaced or refused. */
  readonly onChange: (upload: HrUpload | null) => void;
}

function toUpload(staged: Pick<StagedUpload, 'uploadId' | 'recordId'>, status: HrUploadStatus): HrUpload {
  return {
    uploadId: staged.uploadId,
    ...(staged.recordId === undefined ? {} : { recordId: staged.recordId }),
    fileName: status.fileName,
    sha256: status.sha256,
    byteLength: status.byteLength,
    usable: status.state === 'accepted' && scanPermitsUse(status.scan),
  };
}

/**
 * A file HR attaches, through quarantine (#188, ADR-009 §2). The same path as a candidate's upload:
 * the bytes go to quarantine, are checked on arrival, and wait for the malware scan. Only a clean
 * file can be saved against a record or published as a template.
 */
export function HrFileUpload({ label, hint, required, target, acceptedTypes, disabled, onChange }: HrFileUploadProps) {
  const styles = useStyles();
  /**
   * Fluent's `Field` labels its child by cloning props into it, and only its OWN controls consume
   * them — a bare `<input type="file">` does not, so the label rendered beside this input was not
   * attached to it. A screen reader announced "Choose File" with no field name, and the browser
   * QA snapshot showed it as an unlabelled button next to a `generic` holding the words. Naming
   * the input and pointing the label at it fixes both.
   */
  const inputId = useId('hr-file-upload-');
  const [staged, setStaged] = useState<StagedUpload | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const status = useUploadStatus(target, staged?.uploadId ?? null);
  const current = status.data ?? staged?.status;

  useEffect(() => {
    if (staged !== null && current !== undefined) onChange(toUpload(staged, current));
    // `onChange` is the parent's setter; re-running on its identity would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [staged, current?.scan, current?.state]);

  return (
    <Field label={{ children: label, htmlFor: inputId }} hint={hint} required={required} validationMessage={error || undefined}>
      <input
        id={inputId}
        type="file"
        accept={acceptAttribute(acceptedTypes)}
        disabled={disabled || busy}
        onChange={async (event) => {
          const file = event.target.files?.[0];
          if (!file) return;
          setBusy(true);
          setError('');
          setStaged(null);
          onChange(null);
          try {
            setStaged(await stageUpload(target, file));
          } catch (e) {
            setError(e instanceof Error ? e.message : 'That upload did not work. Please try again.');
          } finally {
            setBusy(false);
          }
        }}
      />
      {busy ? <div className={styles.status}><Spinner size="tiny" /><Caption1>Uploading…</Caption1></div> : null}
      {current === undefined ? null : (
        <Caption1 role="status">{current.state === 'accepted' ? wording[current.scan] : 'That upload did not finish. Upload it again.'}</Caption1>
      )}
    </Field>
  );
}
